import { ComponentType, FunctionComponent, JSX } from 'react'
import styles from './indicator.module.scss'
import { Badge, BadgeProps, styled } from '@mui/material'
import {
    CheckCircle,
    ContactPage,
    Desk,
    DoDisturbOn,
    Error,
    EventBusy,
    HourglassBottom,
    LockOutlined,
    School,
    Warning,
    WatchLater
} from '@mui/icons-material'

interface IndicatorProps {
    count: number
    text?: string
    backgroundWhite?: boolean
    parent?: boolean
    size?: 's' | 'm' | 'l'
    className?: string
    badge?:
        | 'booked'
        | 'available'
        | 'schedule'
        | 'request'
        | 'warning'
        | 'error'
        | 'regular'
        | 'absence'
        | 'regularRequest'
        | 'inSection'
        | 'notInSection'
        | 'staffHoliday'
        | 'staffTraining'
}

const Indicator: FunctionComponent<IndicatorProps> = ({
    count,
    text,
    backgroundWhite,
    parent = false,
    size = 'l',
    className,
    badge
}: IndicatorProps) => {
    const StyledBadge: ComponentType<BadgeProps> = styled(Badge)<BadgeProps>(() => ({
        '& .MuiBadge-badge': {
            right: -1,
            top: 50,
            borderRadius: '50%',
            padding: '15px 3px',
            backgroundColor: 'white',
            boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0px 5px 0px',
            fontSize: '2rem'
        }
    }))

    function badgeIcon(): JSX.Element {
        switch (badge) {
            case 'booked':
            case 'inSection':
                return <CheckCircle />
            case 'available':
                return <WatchLater />
            case 'schedule':
                return <LockOutlined />
            case 'request':
                return <HourglassBottom />
            case 'regular':
                return <ContactPage />
            case 'absence':
                return <DoDisturbOn />
            case 'regularRequest':
                return <HourglassBottom />
            case 'staffTraining':
                return <School />
            case 'staffHoliday':
                return <EventBusy />
            case 'notInSection':
                return <Desk />
            case 'warning':
                return <Warning />
            case 'error':
                return <Error />
            default:
                return <CheckCircle />
        }
    }

    function defineColor(): string {
        switch (badge) {
            case 'booked':
            case 'inSection':
                return styles.Booked
            case 'available':
                return styles.Available
            case 'schedule':
                return styles.Schedule
            case 'request':
                return styles.Request
            case 'regular':
                return styles.Regular
            case 'absence':
                return styles.Absence
            case 'regularRequest':
                return styles.RegularRequest
            case 'staffTraining':
                return styles.StaffTraining
            case 'staffHoliday':
                return styles.StaffHoliday
            case 'notInSection':
                return styles.NotInSection
            case 'warning':
                return styles.Warning
            case 'error':
                return styles.Error
            default:
                return styles.Error
        }
    }

    return (
        <div
            className={`${styles.Indicator} ${backgroundWhite && styles.BackgroundWhite}
		${parent ? styles.ParentIndicator : styles.DaycareIndicator} ${className}`}
        >
            <div
                className={`${styles.Count} ${size === 's' && styles.CountSmall} 
			${size === 'm' && styles.CountMedium} ${size === 'l' && styles.CountLarge} `}
            >
                {badge ? (
                    <StyledBadge
                        badgeContent={badgeIcon()}
                        className={`${parent ? styles.ParentBadge : styles.DaycareBadge} ${defineColor()}`}
                    >
                        <span className={defineColor()}>{count}</span>
                    </StyledBadge>
                ) : (
                    count
                )}
            </div>
            <div
                className={`${styles.Text} ${size === 's' && styles.TextSmall} 
			${size === 'm' && styles.TextMedium} ${size === 'l' && styles.TextLarge}`}
            >
                {text}
            </div>
        </div>
    )
}

export default Indicator
